/**
 * InvoicesSection Component
 *
 * Displays a paginated list of subscription invoices with links to view each invoice on Stripe.
 */

import { useCallback, useRef, useState } from 'react';
import { useTableScrollIndicators } from '@hooks/useTableScrollIndicators';
import { Download } from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  Link,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';

import type { SubscriptionInvoice } from '../store/api';
import { useGetInvoicesQuery } from '../store/api';

const INVOICES_PER_PAGE = 10;

// Format date to readable string
const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

// Format currency from cents
const formatCurrency = (amountCents: number, currency = 'usd'): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency.toUpperCase(),
  }).format(amountCents / 100);
};

// Get status chip color
const getStatusColor = (status: string): 'success' | 'warning' | 'error' | 'default' | 'info' => {
  switch (status.toLowerCase()) {
    case 'paid':
      return 'success';
    case 'open':
    case 'draft':
      return 'info';
    case 'uncollectible':
    case 'void':
      return 'error';
    case 'past_due':
      return 'warning';
    default:
      return 'default';
  }
};

// Format status text
const formatStatus = (status: string): string => {
  return status.charAt(0).toUpperCase() + status.slice(1).replace(/_/g, ' ');
};

interface InvoiceRowProps {
  invoice: SubscriptionInvoice;
}

function InvoiceRow({ invoice }: InvoiceRowProps) {
  const statusColor = getStatusColor(invoice.status);

  return (
    <TableRow>
      <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDate(invoice.created)}</TableCell>
      <TableCell sx={{ whiteSpace: 'nowrap' }}>{invoice.number ?? '—'}</TableCell>
      <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
        {invoice.total !== undefined && invoice.total < 0
          ? `(${formatCurrency(Math.abs(invoice.total))})`
          : formatCurrency(
              invoice.total !== undefined ? invoice.total : (invoice.amount_paid ?? invoice.amount_due),
            )}
      </TableCell>
      <TableCell sx={{ whiteSpace: 'nowrap' }}>
        <Chip
          label={formatStatus(invoice.status)}
          size="small"
          sx={{
            height: 24,
            fontWeight: 600,
            '& .MuiChip-label': { fontSize: '0.75rem' },
            backgroundColor: theme => {
              if (statusColor === 'default') return alpha(theme.palette.grey[500], 0.1);
              return alpha(theme.palette[statusColor].main, 0.1);
            },
            color: theme => {
              if (statusColor === 'default') return theme.palette.grey[700];
              return theme.palette[statusColor].dark;
            },
            border: '1px solid',
            borderColor: theme => {
              if (statusColor === 'default') return theme.palette.grey[500];
              return theme.palette[statusColor].dark;
            },
          }}
        />
      </TableCell>
      <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
        {invoice.invoice_pdf ? (
          <Link
            href={invoice.invoice_pdf}
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.5,
              textDecoration: 'none',
              '&:hover': { textDecoration: 'underline' },
            }}
          >
            Download PDF
            <Download sx={{ fontSize: 14 }} />
          </Link>
        ) : (
          <Typography variant="body2" color="text.secondary">
            —
          </Typography>
        )}
      </TableCell>
    </TableRow>
  );
}

function LoadingRows() {
  return (
    <>
      {Array.from({ length: 3 }).map((_, i) => (
        <TableRow key={i}>
          <TableCell>
            <Skeleton variant="text"  sx={{ width: 100 }}/>
          </TableCell>
          <TableCell>
            <Skeleton variant="text"  sx={{ width: 80 }}/>
          </TableCell>
          <TableCell align="right">
            <Skeleton variant="text" sx={{width: 70,  ml: 'auto' }} />
          </TableCell>
          <TableCell>
            <Skeleton variant="rounded"  sx={{ width: 60, height: 24 }}/>
          </TableCell>
          <TableCell align="right">
            <Skeleton variant="text" sx={{width: 50,  ml: 'auto' }} />
          </TableCell>
        </TableRow>
      ))}
    </>
  );
}

export interface InvoicesSectionProps {
  /**
   * Omit the card's own "Invoices" header when an enclosing surface already
   * provides a title (e.g. `InvoiceHistoryModal`'s dialog title).
   */
  hideHeader?: boolean;
}

export function InvoicesSection({ hideHeader = false }: InvoicesSectionProps) {
  // Track all loaded invoices and pagination cursor
  const [allInvoices, setAllInvoices] = useState<SubscriptionInvoice[]>([]);
  const [cursor, setCursor] = useState<string | undefined>(undefined);
  const [hasMoreState, setHasMoreState] = useState(true);

  // Scroll indicator hook
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const { showLeftIndicator, showRightIndicator } = useTableScrollIndicators(tableContainerRef);

  // Initial query (no cursor)
  const {
    data: initialData,
    isLoading: isInitialLoading,
    error,
  } = useGetInvoicesQuery({ limit: INVOICES_PER_PAGE });

  // Query for additional pages (only when cursor is set)
  const { data: paginatedData, isFetching: isPaginatedFetching } = useGetInvoicesQuery(
    { limit: INVOICES_PER_PAGE, startingAfter: cursor },
    { skip: !cursor },
  );

  // Merge paginated data when it arrives
  const handlePaginatedData = useCallback(() => {
    if (paginatedData?.invoices && cursor) {
      setAllInvoices(prev => {
        // Avoid duplicates
        const existingIds = new Set(prev.map(inv => inv.id));
        const newInvoices = paginatedData.invoices?.filter(inv => !existingIds.has(inv.id)) ?? [];
        return [...prev, ...newInvoices];
      });
      setHasMoreState(paginatedData.has_more);
    }
  }, [paginatedData, cursor]);

  // Effect to merge paginated data
  if (paginatedData?.invoices && cursor && !isPaginatedFetching) {
    const lastLoadedId = allInvoices[allInvoices.length - 1]?.id;
    const lastPaginatedId = paginatedData.invoices[paginatedData.invoices.length - 1]?.id;

    // Only merge if we haven't already merged this page
    if (lastLoadedId !== lastPaginatedId) {
      handlePaginatedData();
    }
  }

  // Determine displayed invoices
  const displayedInvoices = allInvoices.length > 0 ? allInvoices : (initialData?.invoices ?? []);

  // Determine if there are more to load
  const hasMore = allInvoices.length > 0 ? hasMoreState : (initialData?.has_more ?? false);

  const handleLoadMore = () => {
    const lastInvoice = displayedInvoices[displayedInvoices.length - 1];
    if (lastInvoice) {
      // Initialize allInvoices if this is the first "load more"
      if (allInvoices.length === 0 && initialData?.invoices) {
        setAllInvoices(initialData.invoices);
      }
      setCursor(lastInvoice.id);
    }
  };

  const isLoadingMore = isPaginatedFetching && !!cursor;

  if (error) {
    return (
      <Card
        elevation={1}
        sx={{ borderRadius: 2, overflow: 'hidden', border: 1, borderColor: 'grey.200' }}
      >
        {!hideHeader && (
          <CardHeader title="Invoices" titleTypographyProps={{ variant: 'h6', fontWeight: 600 }} />
        )}
        <CardContent>
          <Alert severity="error">Unable to load invoices. Please try again later.</Alert>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card
      elevation={1}
      sx={{ borderRadius: 2, overflow: 'hidden', border: 1, borderColor: 'grey.200' }}
    >
      {!hideHeader && (
        <CardHeader title="Invoices" titleTypographyProps={{ variant: 'h6', fontWeight: 600 }} />
      )}
      <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
        {displayedInvoices.length === 0 && !isInitialLoading ? (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              py: 6,
              px: 3,
            }}
          >
            <Typography variant="body2" color="text.secondary">
              No invoices yet
            </Typography>
          </Box>
        ) : (
          <Box
            sx={{
              // Without the header the table sits flush against the card's own
              // top border — a second line there reads as a double border.
              borderTop: hideHeader ? 'none' : '1px solid',
              borderColor: 'divider',
              overflow: 'hidden',
              position: 'relative',
            }}
          >
            <TableContainer ref={tableContainerRef}>
              <Table>
                <TableHead>
                  <TableRow sx={{ borderBottom: 1, borderColor: 'divider' }}>
                    <TableCell sx={{ bgcolor: 'grey.50', fontWeight: 600, fontSize: '0.875rem' }}>
                      Date
                    </TableCell>
                    <TableCell sx={{ bgcolor: 'grey.50', fontWeight: 600, fontSize: '0.875rem' }}>
                      Invoice #
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{ bgcolor: 'grey.50', fontWeight: 600, fontSize: '0.875rem' }}
                    >
                      Total
                    </TableCell>
                    <TableCell sx={{ bgcolor: 'grey.50', fontWeight: 600, fontSize: '0.875rem' }}>
                      {/* Status column - no header text */}
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{ bgcolor: 'grey.50', fontWeight: 600, fontSize: '0.875rem' }}
                    >
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody
                  sx={{
                    '& tr:last-child td': {
                      borderBottom: 'none',
                    },
                  }}
                >
                  {isInitialLoading ? (
                    <LoadingRows />
                  ) : (
                    displayedInvoices.map(invoice => (
                      <InvoiceRow key={invoice.id} invoice={invoice} />
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            {showLeftIndicator && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  bottom: 0,
                  width: 32,
                  pointerEvents: 'none',
                  background: theme =>
                    `linear-gradient(to right, ${theme.palette.grey[200]}, transparent)`,
                }}
              />
            )}
            {showRightIndicator && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  right: 0,
                  bottom: 0,
                  width: 32,
                  pointerEvents: 'none',
                  background: theme =>
                    `linear-gradient(to left, ${theme.palette.grey[200]}, transparent)`,
                }}
              />
            )}
          </Box>
        )}

        {/* Load More Button */}
        {hasMore && displayedInvoices.length > 0 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
            <Button
              variant="outlined"
              onClick={handleLoadMore}
              disabled={isLoadingMore}
              startIcon={isLoadingMore ? <CircularProgress size={16} /> : undefined}
            >
              {isLoadingMore ? 'Loading...' : 'Load more'}
            </Button>
          </Box>
        )}
      </CardContent>
    </Card>
  );
}
